/** Device preferences only. No account identity or private material belongs here. */
export const PRIVATE_SESSION_IDLE_OPTIONS = [
  300_000, 900_000, 3_600_000,
] as const;
export type PrivateSessionIdleMs =
  (typeof PRIVATE_SESSION_IDLE_OPTIONS)[number];
export const PRIVATE_SESSION_IDLE_MS: PrivateSessionIdleMs = 900_000;

export type PrivateSessionPolicy = {
  idleTimeoutMs: PrivateSessionIdleMs;
  alwaysPrivate: boolean;
  /** Opt-in: a confirmed action waits 3 s and can be undone before sending. */
  undoBeforeSending?: boolean;
};

const KEY = "redacted-private-session-policy";
const defaults = (): PrivateSessionPolicy => ({
  idleTimeoutMs: PRIVATE_SESSION_IDLE_MS,
  alwaysPrivate: false,
});

export const isPrivateSessionIdleMs = (
  value: unknown
): value is PrivateSessionIdleMs =>
  PRIVATE_SESSION_IDLE_OPTIONS.some((duration) => duration === value);

export const requirePrivateSessionIdleMs = (
  value: unknown
): PrivateSessionIdleMs => {
  if (!isPrivateSessionIdleMs(value))
    throw new Error("Invalid private session duration");
  return value;
};

export const readPrivateSessionPolicy = (): PrivateSessionPolicy => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw || raw.length > 256) return defaults();
    const value = JSON.parse(raw);
    if (
      !value ||
      value.version !== 1 ||
      !isPrivateSessionIdleMs(value.idleTimeoutMs) ||
      typeof value.alwaysPrivate !== "boolean" ||
      (value.undoBeforeSending !== undefined &&
        typeof value.undoBeforeSending !== "boolean")
    )
      return defaults();
    return {
      idleTimeoutMs: value.idleTimeoutMs,
      alwaysPrivate: value.alwaysPrivate,
      ...(value.undoBeforeSending === true && { undoBeforeSending: true }),
    };
  } catch {
    return defaults();
  }
};

/** Returns false in restricted browsers; the caller can retain its in-memory
 * preference without pretending it was saved for the next visit. */
export const writePrivateSessionPolicy = (
  policy: PrivateSessionPolicy
): boolean => {
  if (
    !isPrivateSessionIdleMs(policy.idleTimeoutMs) ||
    typeof policy.alwaysPrivate !== "boolean" ||
    (policy.undoBeforeSending !== undefined &&
      typeof policy.undoBeforeSending !== "boolean")
  )
    return false;
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        version: 1,
        idleTimeoutMs: policy.idleTimeoutMs,
        alwaysPrivate: policy.alwaysPrivate,
        ...(policy.undoBeforeSending === true && { undoBeforeSending: true }),
      })
    );
    return true;
  } catch {
    return false;
  }
};
